import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Calendar, Award, Factory, Monitor, GraduationCap, Building2, Hospital, Truck, ArrowRight } from 'lucide-react';
import { TrainingCategories } from '../components/TrainingCategories';
import { UpcomingTrainings } from '../components/UpcomingTrainings';
import { Button } from '../components/ui/button';

const industries = [
  {
    id: 1,
    slug: 'manufacturing',
    icon: Factory,
    name: 'Manufacturing',
    description: 'Quality management and process optimization for manufacturing excellence',
    image: 'https://images.unsplash.com/photo-1754372069872-3fef8a815c87?w=800&q=80'
  },
  {
    id: 2,
    slug: 'information-technology',
    icon: Monitor,
    name: 'Information Technology',
    description: 'IT service management and software quality assurance training',
    image: 'https://images.unsplash.com/flagged/photo-1579274216947-86eaa4b00475?w=800&q=80'
  },
  {
    id: 3,
    slug: 'educational-institutions',
    icon: GraduationCap,
    name: 'Educational Institutions',
    description: 'Faculty development and educational quality management systems',
    image: 'https://images.unsplash.com/photo-1541829070764-84a7d30dd3f3?w=800&q=80'
  },
  {
    id: 4,
    slug: 'healthcare',
    icon: Hospital,
    name: 'Healthcare',
    description: 'Medical device quality and healthcare service management',
    image: 'https://images.pexels.com/photos/40568/medical-appointment-doctor-healthcare-40568.jpeg?w=800&q=80'
  },
  {
    id: 5,
    slug: 'corporate-services',
    icon: Building2,
    name: 'Corporate Services',
    description: 'Management systems and corporate compliance training',
    image: 'https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=800&q=80'
  },
  {
    id: 6,
    slug: 'supply-chain-logistics',
    icon: Truck,
    name: 'Supply Chain & Logistics',
    description: 'Supply chain quality and logistics management excellence',
    image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&q=80'
  }
];

export const TrainingPrograms = () => {
  const navigate = useNavigate();
  const industriesRef = useRef(null);
  const isIndustriesInView = useInView(industriesRef, { once: true, margin: "-100px" });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
    }
  };

  return (
    <div className="pt-20">
      {/* Page Header */}
      <section className="relative py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white overflow-hidden">
        {/* Decorative Background Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-white rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-300 rounded-full filter blur-3xl"></div>
        </div>
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
          backgroundSize: '30px 30px'
        }}></div>

        {/* Floating Cards Animation */}
        <motion.div
          animate={{ y: [0, -15, 0], rotate: [0, 5, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-32 right-20 w-20 h-20 bg-white/10 backdrop-blur-sm rounded-xl hidden lg:block"
        />
        <motion.div
          animate={{ y: [0, 15, 0], rotate: [0, -5, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-32 left-20 w-16 h-16 bg-white/10 backdrop-blur-sm rounded-lg hidden lg:block"
        />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center space-y-6"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
              className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-lg px-6 py-3 rounded-full"
            >
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-sm font-semibold">50+ Active Programs</span>
            </motion.div>
            
            <h1 className="text-5xl lg:text-6xl font-bold">
              Our Training Programs
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
              Comprehensive training solutions designed for Manufacturing, IT, and Educational sectors
            </p>
            
            {/* Quick Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-8">
              <Button
                onClick={() => navigate('/training-calendar')}
                className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all flex items-center gap-3 group"
              >
                <Calendar className="h-5 w-5 group-hover:scale-110 transition-transform" />
                View Training Calendar
              </Button>
              <Button
                onClick={() => navigate('/validate-certificate')}
                variant="outline"
                className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all flex items-center gap-3 group"
              >
                <Award className="h-5 w-5 group-hover:scale-110 transition-transform" />
                Validate Certificate
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Industry Training Section */}
      <section ref={industriesRef} className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isIndustriesInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16 space-y-4"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">
              Industries <span className="text-blue-600">We Serve</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Delivering specialized training solutions across diverse sectors to meet unique industry requirements
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isIndustriesInView ? "visible" : "hidden"}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {industries.map((industry) => {
              const Icon = industry.icon;
              return (
                <motion.div
                  key={industry.id}
                  variants={itemVariants}
                  className="group relative h-80 rounded-2xl hover:shadow-2xl transition-all duration-300 cursor-pointer overflow-hidden"
                  onClick={() => navigate(`/industry-training/${industry.slug}`)}
                >
                  {/* Background Image */}
                  <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                    style={{ backgroundImage: `url(${industry.image})` }}
                  />

                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/70 to-transparent opacity-90 group-hover:opacity-95 transition-opacity duration-300" />

                  {/* Content */}
                  <div className="relative h-full p-8 flex flex-col justify-end text-white z-10">
                    <motion.div
                      className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mb-6 group-hover:bg-blue-600 transition-all duration-300"
                      whileHover={{ scale: 1.1, rotate: 5 }}
                    >
                      <Icon className="h-8 w-8 text-white" />
                    </motion.div>
                    <h3 className="text-2xl font-bold mb-3 group-hover:text-blue-400 transition-colors">
                      {industry.name}
                    </h3>
                    <p className="text-gray-200 leading-relaxed mb-4">
                      {industry.description}
                    </p>
                    <div className="flex items-center text-sm font-semibold text-blue-300 group-hover:text-blue-200">
                      View Training Programs
                      <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      <UpcomingTrainings />
    </div>
  );
};
